/**
 * LinguistPro Elite - Main Application
 * Automatic multilingual transcription with speaker diarisation
 * v5.4.0 - Gemini multilingual engine
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranscriptionStore, useSceneDescriptions } from './store/useTranscriptionStore';
import { useAuthStore, useProfile } from './store/useAuthStore';
import { showToast } from './components/Toast';
import { friendlyMessage } from './utils/errorMessages';
import { useTranscription, useDocumentExport } from './hooks';
import { StatBox, DirectiveToggle, ProcessingLog, DocumentConsolidator, LargeFileHelper, MediaPreprocessor, Tooltip, VideoContextPanel } from './components';
import { LanguageSelectModal } from './components/ui/LanguageSelectModal';
import { SpeakerNamer } from './components/SpeakerNamer';
import PaygFloorNotice from './components/PaygFloorNotice';
import PaygFloorConfirm from './components/PaygFloorConfirm';
import { checkAudioUrl, fileErrorAfterLinkOrTabChange } from './services/audioUrl';
import { floorDurationFor, paygFloorUpload, type PaygFloorUpload } from './services/paygFloor';
import { useMediaDuration } from './hooks/useMediaDuration';
import { QualityBadge } from './components/QualityBadge';
import { TranscriptViewer, TranscriptModeToggle } from './components/TranscriptViewer';
import { IntelligencePanel } from './components/IntelligencePanel';
import { VoiceQAPanel } from './components/VoiceQAPanel';
import { MediaProcessor, ExtractedMedia } from './services/mediaProcessor';
import { APP_VERSION, SYSTEM_STATUS } from './config/constants';
import { AppStep, OutputDirectives, InputType, ExportFormat, DirectiveItem, TranscriptViewMode } from './types';

// Supported file types
const SUPPORTED_AUDIO_TYPES = ['audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/m4a', 'audio/aac'];
const SUPPORTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo'];
const SUPPORTED_EXTENSIONS = ['.mp3', '.wav', '.ogg', '.webm', '.m4a', '.aac', '.mp4', '.mov', '.avi'];

// Directive options configuration
const DIRECTIVE_ITEMS: DirectiveItem[] = [
  { id: 'fullForensicAudit', label: 'Full Transcript', desc: 'Word-for-word record', hint: 'Word-for-word transcript with speaker labels and timestamps' },
  { id: 'executiveSummary', label: 'Executive Summary', desc: 'Key points & decisions', hint: 'AI summary of the main discussion points, decisions, and outcomes' },
  { id: 'meetingSentiment', label: 'Sentiment Analysis', desc: 'Tone & mood tracking', hint: 'Detect emotional tone shifts and overall mood of the conversation' },
  { id: 'actionItems', label: 'Action Items', desc: 'Tasks & responsibilities', hint: 'Extract action items, deadlines, and who is responsible' },
  { id: 'speakerTone', label: 'Speaker Tone', desc: 'Communication style', hint: 'Analyse each speaker\'s communication style and emotional patterns' },
  // SPEC-014: Enhanced AI Intelligence types
  // SPEC-031: correctDutch removed — auto-applied when STT returns af/nl
  //           enforceLanguage removed — intelligence always outputs British English via system instruction
  { id: 'meetingMinutes',  label: 'Meeting Minutes',  desc: 'Formal structured minutes',         hint: 'Generate formal meeting minutes in British English business register — includes Agenda, Decisions, Action Items, and Next Steps sections.' },
  { id: 'keyDecisions',   label: 'Key Decisions',    desc: 'Decision table with attribution',    hint: 'Extract and tabulate all formal decisions made during the meeting, attributed to the speaker who confirmed each decision.' },
  { id: 'followupEmail',  label: 'Follow-up Email',  desc: 'Professional email draft',           hint: 'Draft a professional follow-up email in British English summarising decisions and action items — ready to send after light editing.' },
  { id: 'actionItemsV2',  label: 'Action Items Pro', desc: 'Structured tasks with priority',     hint: 'Extract action items with assignee, due date, priority (High/Medium/Low), timestamp discussed, and supporting context quote.' },
  { id: 'multilingualSummary', label: 'Multilingual Summary', desc: 'Summary in meeting language', hint: 'Generate a structured meeting summary via Claude Opus in the transcript\'s own language — ideal for Afrikaans, French, German, Zulu, and code-switching meetings.' },
];

// Phase descriptions for progress display
const PHASE_DESCRIPTIONS: Record<AppStep, { label: string; description: string }> = {
  [AppStep.IDLE]: { label: 'Ready', description: 'System standing by' },
  [AppStep.FETCHING]: { label: 'Fetching', description: 'Retrieving remote asset...' },
  [AppStep.UPLOADING]: { label: 'Processing', description: 'Decoding audio buffers...' },
  [AppStep.IDENTIFYING_SPEAKERS]: { label: 'Phase 1', description: 'Reading speaker names from the video...' },
  [AppStep.TRANSCRIBING]: { label: 'Phase 2', description: 'Verbatim transcription in progress...' },
  [AppStep.RECONCILING]: { label: 'Phase 3', description: 'Identity reconciliation...' },
  [AppStep.REFINING]: { label: 'Phase 4', description: 'Final audit and cleanup...' },
  [AppStep.INTELLIGENCE_GEN]: { label: 'Phase 5', description: 'Intelligence synthesis...' },
  [AppStep.COMPLETED]: { label: 'Complete', description: 'Processing finished successfully' },
  [AppStep.ERROR]: { label: 'Error', description: 'An error occurred' },
};

export type AppMode = 'transcribe' | 'consolidate';

// ── Directive persistence (SPEC-023) ─────────────────────────────────────────

const DEFAULT_DIRECTIVES: OutputDirectives = {
  executiveSummary: true,
  meetingSentiment: false,
  actionItems: true,
  fullForensicAudit: true,
  speakerTone: false,
  meetingMinutes: false,
  keyDecisions: false,
  followupEmail: false,
  actionItemsV2: false,
  multilingualSummary: false,
};

const DIRECTIVES_KEY = 'lp-directives';

function loadDirectives(): OutputDirectives {
  try {
    const saved = localStorage.getItem(DIRECTIVES_KEY);
    if (!saved) return DEFAULT_DIRECTIVES;
    return { ...DEFAULT_DIRECTIVES, ...(JSON.parse(saved) as Partial<OutputDirectives>) };
  } catch {
    return DEFAULT_DIRECTIVES;
  }
}

export default function App() {
  // App mode state
  const [appMode, setAppMode] = useState<AppMode>('transcribe');
  
  // Local UI state
  const [file, setFile] = useState<File | null>(null);
  const [remoteUrl, setRemoteUrl] = useState('');
  const [inputType, setInputType] = useState<InputType>('local');
  const [isDragging, setIsDragging] = useState(false);
  const [customInstructions, setCustomInstructions] = useState('');
  const [outputFileName, setOutputFileName] = useState('Transcript');
  const [exportFormat, setExportFormat] = useState<ExportFormat>('docx');
  const [fileError, setFileError] = useState<string | null>(null);
  const [showLargeFileHelper, setShowLargeFileHelper] = useState(false);
  const [largeFile, setLargeFile] = useState<File | null>(null);
  const [showPreprocessor, setShowPreprocessor] = useState(false);
  /** Controls the pre-transcription language selection modal */
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [preprocessedMedia, setPreprocessedMedia] = useState<ExtractedMedia | null>(null);
  const [preprocessingFile, setPreprocessingFile] = useState<File | null>(null);
  // SPEC-023: Load directives from localStorage (persisted across sessions)
  const [directives, setDirectives] = useState<OutputDirectives>(loadDirectives);

  const [viewMode, setViewMode] = useState<TranscriptViewMode>('dialogue');

  const fileInputRef = useRef<HTMLInputElement>(null);
  useProfile(); // ensures profile is loaded in AuthStore; used by useTranscription pipeline
  const fileDurationS = useMediaDuration(file);
  // Only a chosen local file has a known length: a leftover file must not describe a URL job.
  const floorDurationS = floorDurationFor(inputType, fileDurationS);
  /** Languages confirmed in the modal, held while the customer decides on the pay-as-you-go minimum. */
  const floorStartedRef = useRef(false);
  const [floorPending, setFloorPending] = useState<{ languages: string[]; notice: PaygFloorUpload } | null>(null);

  // Store state
  const {
    step,
    progress,
    systemStatus,
    error,
    setError,
    logs,
    finalTranscript,
    intelligence,
    speakerMap,
    selectedModelId,
    suggestedSpeakerNames,
    visualIdPending,
    quality_grade,
    confidence,
    utterances,
    intelligenceOutputs,
    ttsAudioUrls,
    ttsLoading,
    reset,
    setFinalTranscript,
    setSpeakerMap,
    setTtsAudio,
    setTtsLoading,
  } = useTranscriptionStore();

  const sceneDescriptions = useSceneDescriptions();

  // Hooks
  const { executePipeline, cancelPipeline } = useTranscription();
  const { handleExport } = useDocumentExport();

  // ── Plan 6: TTS handler ──────────────────────────────────────────────────────
  // Called when user clicks "Listen" on an intelligence tab.
  // Uses cached audio from the store; only fetches if not yet synthesised.
  const handleListenClick = useCallback(async (tabId: string, text: string) => {
    if (ttsAudioUrls[tabId]) return; // already cached — AudioPlayer will show it
    setTtsLoading(tabId, true);
    try {
      const res = await fetch('/api/voice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'tts', text }),
      });
      if (!res.ok) throw new Error(`TTS HTTP ${res.status}`);
      const data = await res.json() as { audioBase64: string };
      setTtsAudio(tabId, data.audioBase64);
    } catch (err) {
      console.warn('[TTS] Error:', (err as Error).message);
    } finally {
      setTtsLoading(tabId, false);
    }
  }, [ttsAudioUrls, setTtsAudio, setTtsLoading]);

  // Validate file type
  const validateFile = useCallback((f: File): boolean => {
    const isValidType = SUPPORTED_AUDIO_TYPES.includes(f.type) || SUPPORTED_VIDEO_TYPES.includes(f.type);
    const hasValidExtension = SUPPORTED_EXTENSIONS.some(ext => f.name.toLowerCase().endsWith(ext));
    
    if (!isValidType && !hasValidExtension) {
      setFileError(`Unsupported file type. Supported: ${SUPPORTED_EXTENSIONS.join(', ')}`);
      return false;
    }
    
    // Check if large video needs preprocessing (client-side audio extraction)
    if (MediaProcessor.needsPreprocessing(f)) {
      setPreprocessingFile(f);
      setShowPreprocessor(true);
      setFileError(null);
      return false;
    }
    
    // Check file size (max 100MB for Vercel)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (f.size > maxSize) {
      setLargeFile(f);
      setShowLargeFileHelper(true);
      setFileError(null);
      return false;
    }
    
    setFileError(null);
    return true;
  }, []);

  // SPEC-023: Persist directive preferences to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(DIRECTIVES_KEY, JSON.stringify(directives));
    } catch {
      // localStorage unavailable (private browsing quota) — silent no-op
    }
  }, [directives]);

  // Update filename based on source
  useEffect(() => {
    if (file) {
      const cleanName = file.name.replace(/\.[^/.]+$/, '').replace(/[^a-z0-9_\-]/gi, '_');
      setOutputFileName(cleanName);
    } else if (remoteUrl) {
      try {
        const urlObj = new URL(remoteUrl);
        const name = urlObj.pathname.split('/').pop()?.replace(/\.[^/.]+$/, '') || 'Remote_Asset';
        setOutputFileName(name);
      } catch {
        setOutputFileName('Remote_Asset');
      }
    }
  }, [file, remoteUrl]);

  // Handlers
  const toggleDirective = useCallback((key: keyof OutputDirectives) => {
    setDirectives((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const selectAllDirectives = useCallback(() => {
    // SPEC-031: correctDutch and enforceLanguage removed from directives
    setDirectives({
      executiveSummary: true,
      meetingSentiment: true,
      actionItems: true,
      fullForensicAudit: true,
      speakerTone: true,
      meetingMinutes: false,
      keyDecisions: false,
      followupEmail: false,
      actionItemsV2: false,
      multilingualSummary: false,
    });
  }, []);

  const handleReset = useCallback(() => {
    setFile(null);
    setRemoteUrl('');
    setOutputFileName('Transcript');
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    reset();
  }, [reset]);

  const handleFileSelect = useCallback(async (f: File | null) => {
    if (f && validateFile(f)) {
      // Clear previous results to prevent data contamination across sessions
      if (finalTranscript || intelligence) reset();
      // Snapshot into memory immediately — File objects are live OS references that
      // browser extensions or long delays can invalidate (causes NotReadableError).
      try {
        const buffer = await f.arrayBuffer();
        setFile(new File([buffer], f.name, { type: f.type || 'application/octet-stream', lastModified: f.lastModified }));
      } catch {
        // File is already unreadable at selection time (browser extension or OS permissions).
        // Surface the error immediately rather than letting it fail deep in the pipeline.
        setFileError('This file could not be read. Please re-select it and try again. If the problem persists, try a different browser or disable browser extensions.');
      }
    }
  }, [validateFile, finalTranscript, intelligence, reset]);

  // Handle preprocessing completion
  const handlePreprocessingComplete = useCallback((result: ExtractedMedia) => {
    setPreprocessedMedia(result);
    setShowPreprocessor(false);
    // Create a File-like object from the audio blob
    const audioFile = new File([result.audioBlob], 'extracted-audio.wav', { type: 'audio/wav' });
    setFile(audioFile);
    // Keep preprocessingFile — original video needed for storage upload if WAV exceeds bucket limit
    // Log extracted frames for speaker identification (will be used in pipeline)
    console.log(`Extracted ${result.frames.length} frames for speaker ID, audio: ${(result.processedSize / 1024 / 1024).toFixed(1)}MB`);
  }, []);

  // Store frames for pipeline use
  useEffect(() => {
    if (preprocessedMedia?.frames.length) {
      // Frames available for speaker identification in transcription pipeline
      console.log('Video frames ready for speaker identification:', preprocessedMedia.frames.length);
    }
  }, [preprocessedMedia]);

  const handlePreprocessingCancel = useCallback(() => {
    setShowPreprocessor(false);
    setPreprocessingFile(null);
    setPreprocessedMedia(null);
  }, []);

  const handlePreprocessingError = useCallback((error: string) => {
    setShowPreprocessor(false);
    setPreprocessingFile(null);
    setFileError(`Processing failed: ${error}`);
  }, []);

  /**
   * Step 1: Validate inputs, then open the language-selection modal.
   * The pipeline is not started until the modal is confirmed.
   */
  const handleStart = useCallback(() => {
    // Block streaming platform URLs — they serve HTML, not audio files
    if (inputType === 'url' && remoteUrl) {
      if (/youtube\.com|youtu\.be|spotify\.com|soundcloud\.com|tiktok\.com/i.test(remoteUrl)) {
        setFileError(
          'YouTube and streaming platform URLs are not supported. ' +
          'Please download the audio as an MP3 or WAV file and upload it directly.'
        );
        return;
      }
      const urlCheck = checkAudioUrl(remoteUrl);
      if (urlCheck.ok === false) {
        setFileError(urlCheck.message);
        return;
      }
    }
    setShowLanguageModal(true);
  }, [inputType, remoteUrl]);

  /**
   * Runs the pipeline with meetingLanguages attached to the options.
   */
  const runPipeline = useCallback((meetingLanguages: string[]) => {
    // Clear any existing results before starting a fresh pipeline run
    if (finalTranscript || intelligence) reset();
    // INP fix: the pipeline prologue creates an AudioContext + commits a large
    // render, which blocked the click→paint for ~316ms. Defer the kickoff to a
    // post-task macrotask so the modal-close paints immediately; the heavy work
    // then runs off the interaction's critical path.
    // See docs/dtp-runs/2026-06-01-afrikaans-dutch-and-inp.md.
    setTimeout(() => {
      executePipeline({
        file,
        originalFile: preprocessingFile,
        remoteUrl,
        inputType,
        directives,
        customInstructions,
        modelId: selectedModelId,
        title: outputFileName || undefined,
        meetingLanguages,
      });
    }, 0);
  }, [executePipeline, reset, file, preprocessingFile, remoteUrl, inputType, directives, customInstructions, selectedModelId, outputFileName, finalTranscript, intelligence]);

  /**
   * Step 2: the modal confirmed the languages. A pay-as-you-go job the 40-minute minimum applies to
   * waits for an explicit "Use 40 minutes and start"; everything else starts straight away.
   * The meeting languages are one transcription run (one charged job, one lp_charge_once call).
   */
  const handleConfirmLanguages = useCallback(async (meetingLanguages: string[]) => {
    setShowLanguageModal(false);
    // The database decrements legacy_paid_seconds on every job, so decide on a fresh figure, not a stale one.
    let fresh = false;
    try { fresh = await useAuthStore.getState().refreshBalance(); } catch (err) {
      console.warn('[payg-floor] balance refresh failed; treating legacy balance as 0', err);
    }
    const current = useAuthStore.getState().profile;
    if (!current) {
      // Plan unknown: never start a job that might carry a charge the customer has not confirmed.
      showToast('Your account is still loading. Please try again in a moment.', 'error');
      return;
    }
    const notice = paygFloorUpload({
      plan: current.plan,
      durationSeconds: floorDurationS,
      balanceSeconds: current.balance_seconds,
      // A failed refresh leaves a cached legacy figure that may be stale (the database decrements it on
      // every job): count it as 0 so the customer is asked to confirm rather than trusted to a stale figure.
      legacySeconds: fresh ? current.legacy_paid_seconds : 0,
    });
    if (notice.requiresConfirm) {
      floorStartedRef.current = false;
      setFloorPending({ languages: meetingLanguages, notice });
      return;
    }
    runPipeline(meetingLanguages);
  }, [floorDurationS, runPipeline]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileSelect(f);
  }, [handleFileSelect]);

  const onExport = useCallback(() => {
    handleExport(exportFormat, outputFileName);
  }, [handleExport, exportFormat, outputFileName]);

  // Computed values
  const isProcessing = step !== AppStep.IDLE && step !== AppStep.COMPLETED && step !== AppStep.ERROR;
  const currentPhase = PHASE_DESCRIPTIONS[step];
  /** True only when ALL background tasks (incl. visual speaker ID) are fully done */
  const allTasksComplete = step === AppStep.COMPLETED && !visualIdPending;
  /** Lock UI interactions while processing OR while background speaker ID still runs */
  const isLocked = isProcessing || visualIdPending;
  const canStart = !!(file || remoteUrl) && !isLocked && !fileError;

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Word count for the transcript
  const transcriptWordCount = useMemo(() => {
    if (!finalTranscript) return 0;
    // Strip timestamps and speaker labels, count remaining words
    const cleaned = finalTranscript
      .replace(/\[\d{1,2}:\d{2}\]/g, '')
      .replace(/\*\*[^*]+\*\*/g, '');
    return cleaned.split(/\s+/).filter(Boolean).length;
  }, [finalTranscript]);

  // TranscriptViewer handles all rendering modes (dialogue/paragraph/verbatim/review)

  return (
    <div className="min-h-screen bg-[#f0f4f8] pb-24 selection:bg-[#00d4b8]/20 selection:text-[#00d4b8]">
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-[#00d4b8] focus:text-[#0d1117] focus:rounded-lg"
      >
        Skip to main content
      </a>

      {/* Progress Bar */}
      <div
        className="fixed top-0 left-0 w-full h-1.5 z-[100] bg-[#e2e8f0] backdrop-blur-sm"
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Processing progress"
      >
        <div
          className="h-full bg-gradient-to-r from-[#00bfa8] via-[#00d4b8] to-[#00e5c8] transition-all duration-1000 ease-in-out shadow-[0_0_20px_#00d4b8]"
          style={{ width: `${progress}%` }}
        />
        {isProcessing && (
          <div className="absolute top-0 h-full w-40 bg-white/30 skew-x-[35deg] animate-shimmer" />
        )}
      </div>

      {/* Floating error toast — always visible above all content */}
      {error && error !== 'NO_BALANCE' && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[200] w-[90vw] max-w-lg animate-in"
          role="alert"
          aria-live="assertive"
        >
          <div className="bg-rose-600 text-white px-6 py-4 rounded-2xl shadow-2xl border border-rose-700 flex items-start gap-3">
            <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-black uppercase tracking-widest mb-1 text-rose-200">Error</p>
              <p className="text-[13px] font-bold leading-snug">{friendlyMessage(error || '', error || undefined)}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="shrink-0 text-rose-200 hover:text-white transition-colors"
              aria-label="Dismiss error"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-8 sm:space-y-12">
        {/* Header */}
        <header className="flex flex-col lg:flex-row justify-between items-start gap-8 lg:gap-12 pb-8 lg:pb-12 border-b border-[#e2e8f0]">
          <div className="relative group">
            <div className="absolute -top-6 sm:-top-8 -left-1 flex flex-wrap items-center gap-2 sm:gap-3">
              <span className="badge bg-[#00d4b8]/10 text-[#0ea5a0] border border-[#00d4b8]/25 shadow-sm">
                {APP_VERSION}
              </span>
              <div 
                className={`flex items-center gap-1.5 badge transition-all duration-500 ${
                  systemStatus === SYSTEM_STATUS.NOMINAL ? 'badge-success' :
                  systemStatus === SYSTEM_STATUS.PROCESSING ? 'badge-info animate-pulse' :
                  systemStatus === SYSTEM_STATUS.RETRYING ? 'badge-warning' :
                  'badge-error'
                }`}
                role="status"
                aria-live="polite"
              >
                <div className={`w-1.5 h-1.5 rounded-full ${
                  systemStatus === SYSTEM_STATUS.NOMINAL ? 'bg-emerald-500' :
                  systemStatus === SYSTEM_STATUS.PROCESSING ? 'bg-[#00d4b8]' :
                  systemStatus === SYSTEM_STATUS.RETRYING ? 'bg-amber-500' :
                  'bg-rose-500'
                }`} />
                {systemStatus}
              </div>
            </div>
            <h1 className="text-4xl sm:text-7xl lg:text-8xl font-black tracking-tighter uppercase italic leading-none text-[#1a2332] group-hover:text-[#1a2332]/80 transition-colors">
              Linguist<span className="text-[#00d4b8]">Pro</span>
            </h1>
            <p className="text-slate-400 text-[10px] sm:text-[12px] font-black tracking-[0.4em] sm:tracking-[0.8em] uppercase mt-2 sm:mt-3 ml-1 sm:ml-2 opacity-60">
              Audio & Video Transcription
            </p>
          </div>

          <div className="flex flex-col items-start lg:items-end gap-6 sm:gap-8 w-full lg:w-auto">
            <div className="flex gap-6 sm:gap-12">
              <StatBox label="Progress" value={progress} unit="%" status={isProcessing ? 'active' : 'neutral'} />
              <StatBox label="Status" value={systemStatus === SYSTEM_STATUS.RETRYING ? 'COOLING' : 'OK'} status={systemStatus === SYSTEM_STATUS.RETRYING ? 'warning' : 'neutral'} />
            </div>
            
            {/* Current Phase Indicator */}
            <div className="flex items-center gap-4 sm:gap-5 w-full lg:w-auto">
              <div className="flex-1 lg:flex-none px-4 sm:px-6 py-3 sm:py-3.5 glass rounded-2xl border border-[#e2e8f0] shadow-xl flex items-center gap-4 sm:gap-5 transition-all duration-700 hover:border-[#00d4b8]/30">
                <div className={`w-2.5 sm:w-3 h-2.5 sm:h-3 rounded-full transition-all duration-1000 ${isProcessing ? 'bg-[#00d4b8] animate-ping' : 'bg-slate-300'}`} />
                <div className="flex flex-col">
                  <span className={`text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] sm:tracking-[0.3em] transition-colors ${isProcessing ? 'text-[#00d4b8]' : 'text-slate-400'}`}>
                    {currentPhase.label}
                  </span>
                  <span className="text-[9px] sm:text-[10px] text-slate-400 tracking-wide">
                    {currentPhase.description}
                  </span>
                </div>
              </div>
              <Tooltip text="Clear all inputs and start fresh">
                <button
                  onClick={handleReset}
                  disabled={isProcessing}
                  className="text-[10px] sm:text-[11px] font-black uppercase text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-all tracking-[0.2em] sm:tracking-[0.3em] px-4 sm:px-6 py-3 sm:py-3.5 rounded-2xl border border-transparent hover:border-rose-500/20 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Reset application"
                >
                  Reset
                </button>
              </Tooltip>
            </div>
          </div>
        </header>

        {/* Mode Toggle */}
        <div className="flex justify-center">
          <div className="flex gap-2 p-1.5 bg-slate-50 rounded-2xl border border-[#e2e8f0]" role="tablist">
            <Tooltip text="Convert audio or video into a text transcript with speaker labels">
              <button
                onClick={() => setAppMode('transcribe')}
                disabled={isProcessing}
                role="tab"
                aria-selected={appMode === 'transcribe'}
                className={`px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-[10px] sm:text-[11px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${
                  appMode === 'transcribe' ? 'bg-[#00d4b8] text-[#0d1117] shadow-lg scale-[1.02]' : 'text-slate-400 hover:text-slate-500'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
                Transcribe
              </button>
            </Tooltip>
            <Tooltip text="Merge multiple transcript documents into one">
              <button
                onClick={() => setAppMode('consolidate')}
                disabled={isProcessing}
                role="tab"
                aria-selected={appMode === 'consolidate'}
                className={`px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-[10px] sm:text-[11px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${
                  appMode === 'consolidate' ? 'bg-[#00d4b8] text-[#0d1117] shadow-lg scale-[1.02]' : 'text-slate-400 hover:text-slate-500'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Consolidate
              </button>
            </Tooltip>
          </div>
        </div>

        {appMode === 'transcribe' ? (
        <main id="main-content" className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Left Panel - Configuration */}
          <div className="lg:col-span-4 space-y-8 lg:space-y-10">
            {/* Asset Upload Section */}
            <section className="card" aria-labelledby="upload-heading">
              <h2 id="upload-heading" className="text-[11px] sm:text-[12px] font-black text-slate-400 uppercase tracking-[0.3em] sm:tracking-[0.4em] mb-8 sm:mb-10">
                Upload
              </h2>

              {/* Input type toggle */}
              <div className="flex gap-2 p-1.5 bg-slate-50 rounded-[20px] sm:rounded-[24px] mb-8 sm:mb-10 border border-gray-100" role="tablist">
                {(['local', 'url'] as InputType[]).map((t) => (
                  <button
                    key={t}
                    onClick={() => {
                      setInputType(t);
                      setFileError((cur) => fileErrorAfterLinkOrTabChange(cur));
                    }}
                    disabled={isProcessing}
                    role="tab"
                    aria-selected={inputType === t}
                    aria-controls={`${t}-panel`}
                    className={`flex-1 py-3 sm:py-4 rounded-2xl text-[10px] sm:text-[11px] font-black uppercase tracking-widest transition-all ${
                      inputType === t ? 'bg-[#00d4b8] text-[#0d1117] shadow-xl scale-[1.02]' : 'text-slate-400 opacity-60 hover:opacity-100'
                    }`}
                  >
                    {t === 'local' ? 'File' : 'URL'}
                  </button>
                ))}
              </div>

              {inputType === 'local' ? (
                <div
                  id="local-panel"
                  role="tabpanel"
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => !isProcessing && fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-[24px] sm:rounded-[32px] p-8 sm:p-12 text-center transition-all duration-500 cursor-pointer ${
                    isProcessing ? 'opacity-40 cursor-not-allowed' :
                    isDragging ? 'border-[#00d4b8] bg-[#00d4b8]/5 scale-[1.02]' :
                    'hover:bg-slate-50 hover:border-slate-300'
                  } ${file ? 'border-[#00d4b8]/60 bg-[#00d4b8]/5' : 'border-[#e2e8f0]'} ${fileError ? 'border-rose-500/50 bg-rose-500/5' : ''}`}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && !isProcessing && fileInputRef.current?.click()}
                  aria-label="Drop zone for file upload"
                >
                  <input 
                    type="file" 
                    ref={fileInputRef} 
                    className="hidden" 
                    onChange={(e) => handleFileSelect(e.target.files?.[0] || null)} 
                    accept={[...SUPPORTED_AUDIO_TYPES, ...SUPPORTED_VIDEO_TYPES].join(',')}
                  />
                  
                  {/* Upload icon */}
                  <div className="mb-4">
                    <svg className="w-10 h-10 mx-auto text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  
                  <div className="text-[13px] sm:text-[15px] font-black text-[#1a2332] truncate">
                    {file ? file.name : 'Select or Drop File'}
                  </div>
                  
                  {file && (
                    <div className="text-[10px] font-bold text-[#00d4b8] mt-2">
                      {formatFileSize(file.size)}
                    </div>
                  )}
                  
                  <div className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">
                    MP3, WAV, M4A, MP4, MOV, AVI, WebM
                  </div>
                </div>
              ) : (
                <div id="url-panel" role="tabpanel">
                  <input
                    type="url"
                    placeholder="https://example.com/audio.mp3"
                    value={remoteUrl}
                    onChange={(e) => {
                      setRemoteUrl(e.target.value);
                      setFileError((cur) => fileErrorAfterLinkOrTabChange(cur));
                    }}
                    disabled={isProcessing}
                    className="input-field font-mono text-[12px] sm:text-[14px]"
                    aria-label="Remote URL input"
                  />
                </div>
              )}

              {/* File error display */}
              {fileError && (
                <div className="mt-4 p-4 bg-rose-500/10 border border-rose-500/30 rounded-2xl">
                  <p className="text-[11px] font-bold text-rose-400">{fileError}</p>
                </div>
              )}
            </section>

            {/* Directives Section */}
            <section className="card space-y-8 sm:space-y-10" aria-labelledby="directives-heading">
              <div className="flex justify-between items-center">
                <h2 id="directives-heading" className="text-[11px] sm:text-[12px] font-black text-slate-400 uppercase tracking-[0.3em] sm:tracking-[0.4em]">
                  Analysis Options
                </h2>
                <Tooltip text="Turn on all analysis options">
                  <button
                    onClick={selectAllDirectives}
                    disabled={isLocked}
                    className="text-[9px] sm:text-[10px] font-black text-[#00d4b8] uppercase hover:underline tracking-widest disabled:opacity-30"
                  >
                    Select All
                  </button>
                </Tooltip>
              </div>

              <div className="grid grid-cols-1 gap-3" role="group" aria-label="Analysis directives">
                {DIRECTIVE_ITEMS.map((item) => (
                  <DirectiveToggle
                    key={item.id}
                    item={item}
                    active={directives[item.id]}
                    onToggle={() => toggleDirective(item.id)}
                    disabled={isLocked}
                  />
                ))}
              </div>

              {/* Custom instructions */}
              <div>
                <Tooltip text="Add specific instructions for the AI — what to focus on, what to extract, or how to format the output">
                  <label htmlFor="custom-instructions" className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">
                    Custom Instructions
                  </label>
                </Tooltip>
                <textarea
                  id="custom-instructions"
                  placeholder="e.g. 'Focus on budget items' or 'Identify compliance risks'"
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  disabled={isProcessing}
                  className="input-field h-28 sm:h-32 resize-none text-[12px] sm:text-[13px]"
                />
              </div>

              {/* Pay-as-you-go minimum, shown before the job can be started */}
              {!isProcessing && canStart && <PaygFloorNotice durationSeconds={floorDurationS} />}

              {/* Start/Stop buttons */}
              {isProcessing ? (
                <button
                  onClick={() => {
                    if (window.confirm('Stop the current transcription? This cannot be undone.')) {
                      cancelPipeline();
                    }
                  }}
                  className="w-full py-7 sm:py-9 rounded-[28px] sm:rounded-[36px] text-[11px] sm:text-[13px] tracking-[0.4em] sm:tracking-[0.6em] bg-rose-600 hover:bg-rose-700 text-white font-black uppercase transition-all"
                >
                  STOP
                </button>
              ) : (
                <button
                  onClick={handleStart}
                  disabled={!canStart}
                  className="btn-primary w-full py-7 sm:py-9 rounded-[28px] sm:rounded-[36px] text-[11px] sm:text-[13px] tracking-[0.4em] sm:tracking-[0.6em]"
                  aria-describedby={!canStart ? 'start-help' : undefined}
                >
                  START TRANSCRIPTION
                </button>
              )}
              {!canStart && !isProcessing && (
                <p id="start-help" className="text-[10px] text-slate-400 text-center">
                  {fileError ? 'Please fix the file error above' : 'Select a file or enter a URL to begin'}
                </p>
              )}
            </section>

            {/* Error display — inline position in panel */}
            {error && error !== 'NO_BALANCE' && (
              <div
                className="p-8 sm:p-10 bg-rose-500/10 border border-rose-500/30 rounded-[32px] sm:rounded-[40px] border-l-[12px] sm:border-l-[16px] border-l-rose-500"
                role="alert"
              >
                <h4 className="text-[12px] sm:text-[14px] font-black text-rose-400 uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-3 sm:mb-4">
                  Error
                </h4>
                <p className="text-[11px] sm:text-[12px] font-bold text-rose-400/80">{friendlyMessage(error || '', error || undefined)}</p>
              </div>
            )}
          </div>

          {/* Right Panel - Output */}
          <div className="lg:col-span-8 space-y-8 lg:space-y-10">
            <ProcessingLog logs={logs} isProcessing={isProcessing} />

            {/* Output Terminal */}
            <div className="card !rounded-[40px] sm:!rounded-[56px] overflow-hidden min-h-[300px] sm:min-h-[950px] !p-0">
              {/* Terminal header */}
              <div className="px-6 sm:px-12 py-8 sm:py-12 border-b border-[#e2e8f0] flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6 sm:gap-8">
                <div className="flex items-center gap-4 sm:gap-6">
                  <div className={`w-4 sm:w-5 h-4 sm:h-5 rounded-full transition-all ${finalTranscript ? 'bg-[#00d4b8] animate-pulse' : 'bg-slate-200'}`} />
                  <div>
                    <h3 className="text-[13px] sm:text-[16px] font-black uppercase tracking-[0.3em] sm:tracking-[0.5em] text-[#1a2332]">
                      Transcript Output
                    </h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <p className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-[0.3em] sm:tracking-[0.4em]">
                        {finalTranscript && transcriptWordCount > 0
                          ? `${transcriptWordCount.toLocaleString()} words · Speaker-labelled`
                          : 'Speaker-labelled transcript'}
                      </p>
                      {quality_grade && (
                        <QualityBadge
                          grade={quality_grade as 'A' | 'B' | 'C' | 'D'}
                          avgConf={confidence ?? undefined}
                          showLabel
                        />
                      )}
                    </div>
                  </div>
                </div>

                {finalTranscript && (
                  <div className="flex flex-col gap-3 w-full xl:w-auto">
                    <input
                      type="text"
                      value={outputFileName}
                      onChange={(e) => setOutputFileName(e.target.value)}
                      className="input-field !py-3 sm:!py-4 !px-4 sm:!px-6 text-[11px] sm:text-[12px] uppercase tracking-widest w-full xl:w-72"
                      aria-label="Output filename"
                    />
                    <div className="flex gap-2 sm:gap-3 w-full">
                      <select
                        value={exportFormat}
                        onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
                        className="input-field !py-3 sm:!py-4 !px-4 sm:!px-6 text-[10px] sm:text-[11px] uppercase min-w-[100px]"
                        aria-label="Export format"
                      >
                        <option value="docx">DOCX</option>
                        <option value="srt">SRT</option>
                        <option value="clipboard">Clipboard</option>
                      </select>
                      <button
                        onClick={onExport}
                        disabled={!allTasksComplete}
                        className={`btn-primary flex-1 sm:flex-none px-6 sm:px-10 py-3 sm:py-4 rounded-2xl text-[10px] sm:text-[12px] tracking-widest whitespace-nowrap transition-opacity ${!allTasksComplete ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {visualIdPending ? (
                          <span className="flex items-center gap-2">
                            <span className="w-3 h-3 border-[2px] border-gray-200 border-t-gray-600 rounded-full animate-spin inline-block" />
                            Identifying...
                          </span>
                        ) : 'Export'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Content area */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-12 lg:p-24 min-h-[200px] sm:min-h-[600px]">
                {speakerMap && (
                  <div className="mb-12 sm:mb-20 p-8 sm:p-12 bg-white rounded-[32px] sm:rounded-[48px] border border-[#e2e8f0] border-l-[10px] sm:border-l-[14px] border-l-[#00d4b8] animate-in">
                    <h4 className="text-[10px] sm:text-[11px] font-black text-slate-400 uppercase tracking-[0.4em] sm:tracking-[0.6em] mb-6 sm:mb-8">
                      Speaker Map
                    </h4>
                    <div className="text-[13px] sm:text-[14px] leading-relaxed text-[#1a2332] font-normal whitespace-pre-wrap">
                      {speakerMap}
                    </div>
                  </div>
                )}

                {visualIdPending && step === AppStep.COMPLETED && finalTranscript && (
                  <div className="mb-8 sm:mb-12 flex items-center gap-3 text-[10px] sm:text-[11px] font-black uppercase tracking-[0.3em] text-[#f59e0b]">
                    <span className="w-4 h-4 border-[2px] border-[#f59e0b]/30 border-t-[#f59e0b] rounded-full animate-spin shrink-0" />
                    Identifying speaker names — export available when complete
                  </div>
                )}
                {step === AppStep.COMPLETED && finalTranscript && !visualIdPending && (
                  <SpeakerNamer
                    transcript={finalTranscript}
                    speakerMap={speakerMap}
                    suggestedNames={suggestedSpeakerNames}
                    onApply={(updatedTranscript, updatedSpeakerMap) => {
                      setFinalTranscript(updatedTranscript);
                      setSpeakerMap(updatedSpeakerMap);
                    }}
                  />
                )}

                {sceneDescriptions && sceneDescriptions.length > 0 && (
                  <VideoContextPanel scenes={sceneDescriptions} />
                )}

                {(intelligence || Object.keys(intelligenceOutputs).length > 0) && (
                  <IntelligencePanel
                    intelligence={intelligence}
                    outputs={intelligenceOutputs}
                    ttsAudioUrls={ttsAudioUrls}
                    ttsLoading={ttsLoading}
                    onListenClick={handleListenClick}
                  />
                )}

                {/* Plan 6: Voice Q&A — shown once transcript is available */}
                {finalTranscript && step === AppStep.COMPLETED && (
                  <VoiceQAPanel transcript={finalTranscript} />
                )}

                <div className="font-mono text-[12px] sm:text-[13px] leading-relaxed text-slate-600 whitespace-pre-wrap border-l-4 border-[#e2e8f0] pl-6 sm:pl-12">
                  {isProcessing && !finalTranscript && (
                    <div className="flex flex-col items-center justify-center py-20 sm:py-32">
                      <div className="w-20 sm:w-32 h-20 sm:h-32 border-[3px] border-slate-200 border-t-[#00d4b8] rounded-full animate-spin mb-8 sm:mb-12" />
                      <div className="text-[11px] sm:text-[13px] font-black uppercase tracking-[0.8em] sm:tracking-[1.2em] text-[#00d4b8] animate-pulse text-center">
                        {currentPhase.description}
                      </div>
                    </div>
                  )}
                  {finalTranscript && (
                    <>
                      <div className="mb-4 sm:mb-6">
                        <TranscriptModeToggle
                          mode={viewMode}
                          onChange={setViewMode}
                          hasUtterances={!!(utterances && utterances.length > 0)}
                        />
                      </div>
                      <TranscriptViewer
                        transcript={finalTranscript}
                        utterances={utterances}
                        mode={viewMode}
                        onEdit={setFinalTranscript}
                      />
                    </>
                  )}
                  {!isProcessing && !finalTranscript && (
                    <div className="flex flex-col items-center justify-center py-16 sm:py-32 text-slate-400">
                      <svg className="w-12 h-12 sm:w-16 sm:h-16 mb-6 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                      <div className="text-left space-y-3 text-[12px] sm:text-[14px] font-medium max-w-xs">
                        <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.3em] text-slate-300 text-center mb-4">How it works</p>
                        <div className="flex items-start gap-3">
                          <span className="shrink-0 w-6 h-6 rounded-full bg-[#00d4b8]/10 text-[#00d4b8] text-[11px] font-black flex items-center justify-center">1</span>
                          <span>Upload an audio or video file</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="shrink-0 w-6 h-6 rounded-full bg-[#00d4b8]/10 text-[#00d4b8] text-[11px] font-black flex items-center justify-center">2</span>
                          <span>Choose your analysis options</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="shrink-0 w-6 h-6 rounded-full bg-[#00d4b8]/10 text-[#00d4b8] text-[11px] font-black flex items-center justify-center">3</span>
                          <span>Click Start Transcription</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="shrink-0 w-6 h-6 rounded-full bg-[#00d4b8]/10 text-[#00d4b8] text-[11px] font-black flex items-center justify-center">4</span>
                          <span>Export when complete</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
        ) : (
        <main id="main-content" className="max-w-4xl mx-auto">
          <DocumentConsolidator />
        </main>
        )}

        {/* Language Selection Modal — shown before each pipeline run */}
        {showLanguageModal && (
          <LanguageSelectModal
            onConfirm={handleConfirmLanguages}
            onCancel={() => setShowLanguageModal(false)}
          />
        )}

        {/* Pay-as-you-go minimum: the job starts only when the customer presses the confirm button */}
        {floorPending && (
          <PaygFloorConfirm
            notice={floorPending.notice}
            onCancel={() => setFloorPending(null)}
            onConfirm={() => {
              if (floorStartedRef.current) return; // a double-click must not start the job twice
              floorStartedRef.current = true;
              const languages = floorPending.languages;
              setFloorPending(null);
              runPipeline(languages);
            }}
          />
        )}

        {/* Large File Helper Modal */}
        {showLargeFileHelper && largeFile && (
          <LargeFileHelper
            file={largeFile}
            onClose={() => {
              setShowLargeFileHelper(false);
              setLargeFile(null);
            }}
            onFileReady={(message) => console.log(message)}
          />
        )}

        {/* Media Preprocessor Modal */}
        {showPreprocessor && preprocessingFile && (
          <MediaPreprocessor
            file={preprocessingFile}
            onComplete={handlePreprocessingComplete}
            onCancel={handlePreprocessingCancel}
            onError={handlePreprocessingError}
          />
        )}
      </div>
    </div>
  );
}
